import { useState } from "react";
import { trpc } from "../../lib/trpc.js";
import { Lista, Pagina } from "../../components/Pagina.js";
import {
  Botao,
  Campo,
  Entrada,
  Falha,
  Girando,
  Selecao,
  Selo,
  Vazio,
} from "../../components/ui.js";
import { real } from "../../lib/formato.js";
import type { SaidaRota } from "../../lib/trpc.js";

type Pacote = SaidaRota["pacotes"]["listar"][number];

/**
 * O catálogo e quem tem o quê.
 *
 * Duas coisas na mesma tela porque são a mesma decisão vista de dois lados: o
 * que existe para vender ou conceder, e o que já foi para a mão de cada
 * cliente. Separá-las obrigaria a navegar entre elas para responder "esta casa
 * ainda tem foto?".
 */
export function PacotesPage() {
  const [criando, setCriando] = useState(false);
  const [editando, setEditando] = useState<Pacote | null>(null);

  const catalogo = trpc.pacotes.listar.useQuery();
  const concedidos = trpc.pacotes.porCliente.useQuery();

  return (
    <Pagina
      titulo="Pacotes"
      descricao="O que se vende, o que se concede, e quanto cada cliente ainda tem."
      acao={
        <Botao
          tom="brasa"
          onClick={() => {
            setEditando(null);
            setCriando((v) => !v);
          }}
        >
          {criando ? "Cancelar" : "Novo pacote"}
        </Botao>
      }
    >
      {(criando || editando) && (
        <Formulario
          pacote={editando}
          aoFechar={() => {
            setCriando(false);
            setEditando(null);
          }}
        />
      )}

      <section className="mb-8">
        <h2 className="mb-2 text-[0.8125rem] font-medium text-texto-suave">Catálogo</h2>
        {catalogo.isLoading ? (
          <Girando className="mx-auto my-8 size-6 text-texto-fraco" />
        ) : catalogo.isError ? (
          <Falha mensagem={catalogo.error.message} />
        ) : (catalogo.data ?? []).length === 0 ? (
          <Vazio
            titulo="Nenhum pacote"
            descricao="Crie o primeiro para poder vender ou conceder fotos."
          />
        ) : (
          <Lista
            itens={catalogo.data ?? []}
            chave={(p) => p.id}
            colunas={["Nome", "Fotos", "Preço", "Por foto", "Tipo", ""]}
            cartao={(p) => (
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate font-medium">{p.nome}</p>
                  <p className="text-[0.8125rem] text-texto-suave">
                    {p.fotos} fotos · {p.precoCentavos > 0 ? real(p.precoCentavos / 100) : "grátis"}
                  </p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-2">
                  <Selo tom={p.comercializavel ? "fixado" : "projecao"}>
                    {p.comercializavel ? "à venda" : "cortesia"}
                  </Selo>
                  <Botao tom="fantasma" formato="nu" onClick={() => setEditando(p)}>
                    Editar
                  </Botao>
                </div>
              </div>
            )}
            linha={(p) => [
              p.nome,
              <span key="f" className="num">
                {p.fotos}
              </span>,
              <span key="p" className="num">
                {p.precoCentavos > 0 ? real(p.precoCentavos / 100) : "—"}
              </span>,
              <span key="u" className="num text-texto-suave">
                {p.precoCentavos > 0 ? real(p.precoCentavos / 100 / Math.max(1, p.fotos)) : "—"}
              </span>,
              <Selo key="t" tom={p.comercializavel ? "fixado" : "projecao"}>
                {p.comercializavel ? "à venda" : "cortesia"}
              </Selo>,
              <Botao key="a" tom="fantasma" formato="nu" onClick={() => setEditando(p)}>
                Editar
              </Botao>,
            ]}
          />
        )}
      </section>

      <Conceder />

      <section className="mt-8">
        <h2 className="mb-2 text-[0.8125rem] font-medium text-texto-suave">Na mão dos clientes</h2>
        {concedidos.isLoading ? (
          <Girando className="mx-auto my-8 size-6 text-texto-fraco" />
        ) : (concedidos.data ?? []).length === 0 ? (
          <Vazio titulo="Nada concedido ainda" descricao="Use o formulário acima." />
        ) : (
          <Lista
            itens={concedidos.data ?? []}
            chave={(p) => p.id}
            colunas={["Cliente", "Usadas", "Restantes", "Origem", "Situação", ""]}
            cartao={(p) => (
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate font-medium">{p.cliente}</p>
                  <p className="num text-[0.8125rem] text-texto-suave">
                    {p.fotosUsadas} de {p.fotosTotal} usadas
                  </p>
                </div>
                <Selo tom={p.status === "ativo" ? "fixado" : "quieto"}>{p.status}</Selo>
              </div>
            )}
            linha={(p) => [
              p.cliente,
              <span key="u" className="num">
                {p.fotosUsadas} de {p.fotosTotal}
              </span>,
              <span key="r" className="num">
                {Math.max(0, p.fotosTotal - p.fotosUsadas)}
              </span>,
              p.origem,
              <Selo key="s" tom={p.status === "ativo" ? "fixado" : "quieto"}>
                {p.status}
              </Selo>,
              <CancelarPacote key="a" id={p.id} ativo={p.status === "ativo"} />,
            ]}
          />
        )}
      </section>
    </Pagina>
  );
}

/** Concede um pacote a um cliente, sem cobrança. */
function Conceder() {
  const [userId, setUserId] = useState("");
  const [pacoteId, setPacoteId] = useState("");
  const [fotos, setFotos] = useState("");
  const [erro, setErro] = useState<string | null>(null);

  const utils = trpc.useUtils();
  const usuarios = trpc.users.list.useQuery();
  const catalogo = trpc.pacotes.listar.useQuery();

  const conceder = trpc.pacotes.conceder.useMutation({
    onSuccess: () => {
      void utils.pacotes.porCliente.invalidate();
      setUserId("");
      setFotos("");
      setErro(null);
    },
    onError: (e) => setErro(e.message),
  });

  const clientes = (usuarios.data ?? []).filter((u) => u.role === "cliente");

  return (
    <section className="rounded-peca border border-borda bg-superficie p-4">
      <h2 className="mb-3 text-[0.8125rem] font-medium text-texto-suave">Conceder cortesia</h2>

      {clientes.length === 0 ? (
        <p className="text-[0.9375rem] text-texto-suave">
          Nenhuma conta de cliente ainda. Crie uma em Usuários.
        </p>
      ) : (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1">
            <Campo rotulo="Cliente">
              <Selecao value={userId} onChange={(e) => setUserId(e.target.value)}>
                <option value="">Selecione</option>
                {clientes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Selecao>
            </Campo>
          </div>

          <div className="flex-1">
            <Campo rotulo="Pacote">
              <Selecao value={pacoteId} onChange={(e) => setPacoteId(e.target.value)}>
                <option value="">Selecione</option>
                {(catalogo.data ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nome} — {p.fotos} fotos
                  </option>
                ))}
              </Selecao>
            </Campo>
          </div>

          <div className="sm:w-32">
            <Campo rotulo="Fotos" dica="Vazio usa o do pacote.">
              <Entrada
                type="number"
                value={fotos}
                onChange={(e) => setFotos(e.target.value)}
                min={1}
              />
            </Campo>
          </div>

          <Botao
            tom="brasa"
            disabled={userId === "" || pacoteId === ""}
            carregando={conceder.isPending}
            onClick={() =>
              conceder.mutate({
                userId: Number(userId),
                pacoteId: Number(pacoteId),
                ...(fotos ? { fotos: Number(fotos) } : {}),
              })
            }
          >
            Conceder
          </Botao>
        </div>
      )}

      {erro && (
        <div className="mt-3">
          <Falha mensagem={erro} />
        </div>
      )}
    </section>
  );
}

function CancelarPacote({ id, ativo }: { id: number; ativo: boolean }) {
  const [confirmando, setConfirmando] = useState(false);
  const utils = trpc.useUtils();
  const cancelar = trpc.pacotes.cancelar.useMutation({
    onSuccess: () => void utils.pacotes.porCliente.invalidate(),
  });

  if (!ativo) return null;

  // Cancelar tira o saldo restante de uma conta em uso: pede confirmação.
  return confirmando ? (
    <span className="flex flex-wrap gap-2">
      <Botao tom="descartar" formato="nu" onClick={() => cancelar.mutate({ id })}>
        Confirmar
      </Botao>
      <Botao tom="fantasma" formato="nu" onClick={() => setConfirmando(false)}>
        Não
      </Botao>
    </span>
  ) : (
    <Botao tom="fantasma" formato="nu" onClick={() => setConfirmando(true)}>
      Cancelar
    </Botao>
  );
}

function Formulario({ pacote, aoFechar }: { pacote: Pacote | null; aoFechar: () => void }) {
  const [nome, setNome] = useState(pacote?.nome ?? "");
  const [fotos, setFotos] = useState(String(pacote?.fotos ?? 100));
  const [preco, setPreco] = useState(
    pacote ? String((pacote.precoCentavos / 100).toFixed(2)) : "250.00",
  );
  const [comercializavel, setComercializavel] = useState(pacote?.comercializavel ?? true);
  const [erro, setErro] = useState<string | null>(null);

  const utils = trpc.useUtils();
  const aoTerminar = () => {
    void utils.pacotes.listar.invalidate();
    aoFechar();
  };

  const criar = trpc.pacotes.criar.useMutation({
    onSuccess: aoTerminar,
    onError: (e) => setErro(e.message),
  });
  const atualizar = trpc.pacotes.atualizar.useMutation({
    onSuccess: aoTerminar,
    onError: (e) => setErro(e.message),
  });

  // Reais na tela, centavos no banco: dinheiro não passa por ponto flutuante.
  const precoCentavos = Math.round(Number(preco.replace(",", ".")) * 100);
  const valido = nome.trim().length >= 2 && Number(fotos) >= 1 && Number.isFinite(precoCentavos);

  function salvar() {
    setErro(null);
    const base = { nome: nome.trim(), fotos: Number(fotos), precoCentavos, comercializavel };
    if (pacote) atualizar.mutate({ id: pacote.id, ...base });
    else criar.mutate(base);
  }

  return (
    <div className="mb-6 flex flex-col gap-4 rounded-peca border border-borda bg-superficie p-4">
      <Campo rotulo="Nome">
        <Entrada value={nome} onChange={(e) => setNome(e.target.value)} autoFocus />
      </Campo>

      <div className="grid gap-4 sm:grid-cols-2">
        <Campo rotulo="Fotos">
          <Entrada type="number" value={fotos} onChange={(e) => setFotos(e.target.value)} min={1} />
        </Campo>
        <Campo
          rotulo="Preço (R$)"
          dica={
            Number(fotos) > 0 && precoCentavos > 0
              ? `${real(precoCentavos / 100 / Number(fotos))} por foto`
              : "Zero é cortesia."
          }
        >
          <Entrada value={preco} onChange={(e) => setPreco(e.target.value)} inputMode="decimal" />
        </Campo>
      </div>

      <Campo rotulo="Disponibilidade" dica="Cortesia não aparece na vitrine do cliente.">
        <Selecao
          value={comercializavel ? "venda" : "cortesia"}
          onChange={(e) => setComercializavel(e.target.value === "venda")}
        >
          <option value="venda">À venda</option>
          <option value="cortesia">Só cortesia</option>
        </Selecao>
      </Campo>

      {erro && <Falha mensagem={erro} />}

      <div className="flex gap-2">
        <Botao
          tom="brasa"
          onClick={salvar}
          disabled={!valido}
          carregando={criar.isPending || atualizar.isPending}
        >
          {pacote ? "Salvar" : "Criar"}
        </Botao>
        <Botao tom="quieto" onClick={aoFechar}>
          Cancelar
        </Botao>
      </div>
    </div>
  );
}
